/**
 * cloud.jeket.com surfaces (JSS#1154, epic #1162): the one registry the
 * header's surface switcher and the left sidebar both render from.
 *
 * A surface is a product area (Streaming, Media, Sites, ...) with its own
 * route prefix and a list of sidebar items. Add a page by adding an item
 * here and a route in App.tsx; never hard-code a nav list elsewhere.
 *
 * Operations (platform health) is deliberately absent: it is not a customer
 * surface. It lives in the staff console (corp-console, #1156); superusers
 * get a link out to it from the user menu (`CORP_PLATFORM_HEALTH_URL`).
 */
import {
  House,
  Clapperboard,
  Calendar,
  Radio,
  Tv,
  Film,
  Megaphone,
  Shield,
  PlayCircle,
  BarChart3,
  Video,
  ListTodo,
  Scissors,
  Share2,
  Settings,
  Server,
  Cpu,
  Building2,
  User,
  type LucideIcon,
} from 'lucide-react';
import { canActAs } from '@jeket-cloud/types/auth';
import type { Role } from '@jeket-cloud/types/auth';
import type { EntitledSurface, SurfaceEntitlements } from '@jeket-cloud/types';

export interface SurfaceItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Hide the item from users who cannot act as this role. */
  minRole?: Role;
  /** Overrides the surface's `orgRequired` for this one item. */
  orgRequired?: boolean;
  /**
   * The page calls nothing org-scoped (only /api/auth/*), so it stays
   * reachable for a session acting in no org (#1171): the account page, so a
   * member of several orgs who has not picked one can still see their account.
   */
  orgFree?: boolean;
}

export interface Surface {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Route prefix; every item path starts with it. */
  basePath: string;
  items: SurfaceItem[];
  /** Hide the whole surface from users who cannot act as this role. */
  minRole?: Role;
  /**
   * The surface's data is per-org, so the superuser "All Organizations"
   * view cannot show it: `OrgRequiredGate` (#1149) shows a superuser with
   * no org picked an org picker instead of the page. An item's own
   * `orgRequired` wins over this.
   */
  orgRequired?: boolean;
  /** Ends the gate's "pick one to …" line, e.g. "view its recordings and jobs". */
  orgRequiredHint?: string;
  /**
   * The org entitlement that turns the surface on (#1160): the acting org's
   * `GET /api/me/entitlements` surfaces. Staff-managed until #473 has
   * product data. A surface without one (Home, Organization) is always on.
   */
  entitlement?: EntitledSurface;
  /** Only superusers see the surface. */
  superuserOnly?: boolean;
}

/**
 * Platform health in the staff console (corp-console, k8s/oci/corp-console.yaml
 * Ingress host corp.jeket.com; page route /platform-health). An external
 * link: it signs in with its own Keycloak client and needs `jeket-staff`.
 */
export const CORP_PLATFORM_HEALTH_URL = 'https://corp.jeket.com/platform-health';

export const SURFACES: readonly Surface[] = [
  {
    id: 'home',
    label: 'Home',
    icon: House,
    basePath: '/home',
    items: [{ path: '/home', label: 'Overview', icon: House }],
  },
  {
    id: 'production',
    entitlement: 'production',
    label: 'Production',
    icon: Clapperboard,
    basePath: '/production',
    items: [{ path: '/production', label: 'Productions', icon: Clapperboard }],
  },
  {
    id: 'audience',
    entitlement: 'audience',
    label: 'Audience',
    icon: Calendar,
    basePath: '/audience',
    items: [{ path: '/audience/events', label: 'Events', icon: Calendar }],
  },
  {
    id: 'streaming',
    entitlement: 'streaming',
    label: 'Streaming',
    icon: Radio,
    basePath: '/streaming',
    items: [
      { path: '/streaming/sources', label: 'Sources', icon: Radio },
      { path: '/streaming/channels', label: 'Channels', icon: Tv },
      { path: '/streaming/vod', label: 'VOD', icon: Film },
      { path: '/streaming/ads', label: 'Ads', icon: Megaphone },
      { path: '/streaming/drm', label: 'DRM', icon: Shield },
      { path: '/streaming/player', label: 'Player', icon: PlayCircle },
      { path: '/streaming/analytics', label: 'Viewer analytics', icon: BarChart3 },
    ],
  },
  {
    id: 'media',
    entitlement: 'media',
    label: 'Media',
    icon: Video,
    basePath: '/media',
    // processing-service (Recordings, Jobs, Clip review, Settings) 401s with
    // no X-User-Org, and social-service filters by it, so Social would list
    // nothing and fail to save in All Organizations. All of Media is gated.
    orgRequired: true,
    orgRequiredHint: 'view its recordings and jobs',
    items: [
      { path: '/media/recordings', label: 'Recordings', icon: Video },
      { path: '/media/jobs', label: 'Jobs', icon: ListTodo },
      { path: '/media/clips', label: 'Clip review', icon: Scissors },
      { path: '/media/social', label: 'Social', icon: Share2 },
      { path: '/media/settings', label: 'Settings', icon: Settings },
    ],
  },
  {
    id: 'sites',
    entitlement: 'sites',
    label: 'Sites',
    icon: Server,
    basePath: '/sites',
    items: [
      { path: '/sites', label: 'Sites', icon: Server },
      { path: '/sites/machines', label: 'Machines', icon: Cpu },
    ],
  },
  {
    id: 'org',
    label: 'Organization',
    icon: Building2,
    basePath: '/org',
    items: [{ path: '/org/account', label: 'Account', icon: User, orgFree: true }],
  },
];

/** What the registry needs to know about the viewer. */
export interface SurfaceViewer {
  role?: string;
  roles?: readonly string[];
}

function allowed(
  viewer: SurfaceViewer | null | undefined,
  gate: { minRole?: Role; superuserOnly?: boolean },
): boolean {
  const superuser = viewer?.role === 'superuser';
  if (gate.superuserOnly && !superuser) return false;
  if (gate.minRole && !superuser && !canActAs(viewer?.roles ?? [], gate.minRole)) return false;
  return true;
}

/**
 * Is the surface on for the acting org? Superusers see every surface. No
 * entitlements yet (loading, or the read failed) counts as on: the default is
 * all-on, and the BFF enforces the real answer.
 */
export function surfaceEntitled(
  surface: Pick<Surface, 'entitlement'>,
  viewer: SurfaceViewer | null | undefined,
  entitlements: Partial<SurfaceEntitlements> | null | undefined,
): boolean {
  if (!surface.entitlement || viewer?.role === 'superuser' || !entitlements) return true;
  return entitlements[surface.entitlement] !== false;
}

/**
 * The surfaces this viewer may see, each with only the items they may see:
 * role gates, and the acting org's entitlements (#1160) when given.
 */
export function visibleSurfaces(
  viewer: SurfaceViewer | null | undefined,
  entitlements?: Partial<SurfaceEntitlements> | null,
): Surface[] {
  return SURFACES.filter((s) => allowed(viewer, s) && surfaceEntitled(s, viewer, entitlements))
    .map((s) => ({ ...s, items: s.items.filter((i) => allowed(viewer, i)) }))
    .filter((s) => s.items.length > 0);
}

const underPath = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(base + '/');

/** The surface whose prefix holds `pathname`, if any. */
export function surfaceForPath<S extends Surface>(surfaces: readonly S[], pathname: string): S | undefined {
  return surfaces.find((s) => underPath(pathname, s.basePath));
}

/**
 * The item `pathname` belongs to: the longest item path that prefixes it,
 * so `/sites/machines` is Machines and `/sites/abc` is Sites.
 */
export function activeItem(surface: Surface, pathname: string): SurfaceItem | undefined {
  let best: SurfaceItem | undefined;
  for (const item of surface.items) {
    if (underPath(pathname, item.path) && (!best || item.path.length > best.path.length)) best = item;
  }
  return best;
}

/**
 * Whether `pathname` is an org-required page (#1149): the active item's
 * `orgRequired` when it sets one, else its surface's. False outside a surface.
 */
export function isOrgRequired(pathname: string): boolean {
  const surface = surfaceForPath(SURFACES, pathname);
  if (!surface) return false;
  return activeItem(surface, pathname)?.orgRequired ?? surface.orgRequired ?? false;
}

/**
 * Whether `pathname` is a page whose data comes from org-scoped BFF routes
 * (#1171): any page of a surface item, unless the item is `orgFree`. A
 * member of several orgs with no pick gets 409 org_selection_required from
 * all of them, and staff in no org 403 from the services. Not: paths outside
 * every surface (/event/:slug, /ops, legacy redirects), and a surface's bare
 * prefix (/org, /streaming), which only redirects to an item that is judged
 * on its own.
 */
export function isOrgScoped(pathname: string): boolean {
  const surface = surfaceForPath(SURFACES, pathname);
  if (!surface) return false;
  const item = activeItem(surface, pathname);
  return item !== undefined && !item.orgFree;
}
